import React from "react";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { formatDuration, formatSecondsBrief, pct, resolvedId } from "../../../utils/oversightFormat";

/**
 * Mobile card list for coach analytics (below `sm:`).
 *
 * Renders one card per coach with metrics grid, tap-to-detail action,
 * and a "View rooms" button. Optimized for 320px+ viewport; no horizontal scroll.
 * Card layout: header (avatar + name + online duration) + 2×3 stat grid +
 * bottom "View rooms" action.
 *
 * @param {Object} props
 * @param {Array<Object>} props.coaches - Filtered + sorted coach analytics rows.
 * @param {boolean} props.loading - True while the coaches page is (re)loading.
 * @param {(coach: Object) => void} props.onOpenCoach - Opens a coach's detail page.
 * @param {(coach: Object) => void} props.onViewRooms - Jumps to the Rooms tab filtered to this coach.
 */
export function CoachSummaryCards({ coaches, loading, onOpenCoach, onViewRooms }) {
  const { getCoachName } = useCommsIdentity();

  if (loading) {
    return null;
  }

  const renderStat = (label, value) => (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
        {label}
      </div>
      <div className="text-sm font-semibold text-gray-900">
        {value == null || value === "" ? "—" : value}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {(coaches || []).map((coach, index) => {
        const coachId = resolvedId(coach);
        const coachIdStr = coachId != null ? String(coachId) : null;
        const colorIndex = coachIdStr ? coachIdStr.charCodeAt(0) % 8 : 0;
        const presence = coach.is_online_now ? "online" : "offline";
        const coachName =
          coach.coach_name ||
          getCoachName(coachId) ||
          (coachIdStr ? `Coach ${coachIdStr.slice(-4)}` : "Coach");

        // Format stat values
        const sessionsCount = coach.sessions_count ?? "—";
        const messagesSent = coach.messages_sent ?? "—";
        const medianFR = formatSecondsBrief(coach.median_first_response_seconds);
        const slaHitRate = pct(coach.sla_hit_rate);
        const csatAvg =
          coach.csat_avg != null && !isNaN(coach.csat_avg)
            ? coach.csat_avg.toFixed(1)
            : "—";
        const crisisCount = coach.crisis_count ?? 0;

        return (
          <div
            key={coachId || coach.coach_user_id || index}
            role="button"
            tabIndex={0}
            onClick={() => onOpenCoach(coach)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenCoach(coach);
              }
            }}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-white p-4 cursor-pointer transition-colors hover:bg-gray-50 active:bg-gray-100"
          >
            {/* Header: Avatar, name, online duration */}
            <div className="flex items-start gap-3 mb-3">
              <AvatarRing
                name={coachName}
                colorIndex={colorIndex}
                presence={presence}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">
                  {coachName}
                </div>
              </div>
              <div className="text-right text-xs text-gray-400 whitespace-nowrap ml-2">
                online today{" "}
                {formatDuration(coach.online_seconds_today)}
              </div>
            </div>

            {/* Stats grid: 2 rows × 3 columns */}
            <div className="grid grid-cols-3 gap-y-3">
              {renderStat("Sessions", sessionsCount)}
              {renderStat("Msgs", messagesSent)}
              {renderStat("Median FR", medianFR)}
              {renderStat("SLA", slaHitRate)}
              {renderStat("CSAT", csatAvg)}
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                  Crisis
                </div>
                <div
                  className={`text-sm font-semibold ${
                    crisisCount > 0 ? "text-danger" : "text-gray-900"
                  }`}
                >
                  {crisisCount}
                </div>
              </div>
            </div>

            {/* View rooms button */}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewRooms(coach);
                }}
                className="min-h-[44px] inline-flex items-center px-2 text-xs text-gray-500 font-medium hover:text-orange-primary transition-colors"
              >
                View rooms
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
